import ClassNames from "classnames";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";

import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "../../components/Autocomplete";
import AiPopover from "../AiPopover";
import AiInsightDiv from "../AiInsightDiv";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";

import EditIcon from "@material-ui/icons/Edit";
import LinkIcon from "@material-ui/icons/Link";

import { Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { SubjectableType } from "../../enums/SubjectableType";
import { decimalCheck } from "../../utils/customValidation";
import styles from "./index.module.scss";

const TARGET_FIELD_NAME = "reality_target";
const SKILLS_FIELD_NAME = "skill_improve_id";
const FREETEXT_SKILLS_FIELD_NAME = "skill_improve_text";

function SkillLists({
  currentMetricId,
  skillsRequired,
  onSkillChange,
  onNudge,
  onSkillSelect,
  anchorRef,
  index,
  name,
  ...props
}) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [skillsList, setSkillsList] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);

  async function getSkillList() {
    if (skillsRequired) {
      const response = await apiRequest.get(
        `/api/getSkillToImproveData?subject_metrics_id=${currentMetricId}&session_type_id=${skillsRequired.sessionTypeId}&framework=${skillsRequired.sessionFramework}&method=${skillsRequired.sessionMethod}`
      );
      if (response.status === 200) {
        const data = response.data.data;
        setSkillsList(data);
        if (localStorage.subjectListData) {
          const tmp = JSON.parse(localStorage.subjectListData);
          const defaultSkill = data.find(
            item => item.id === tmp?.subjectList[0]?.[SKILLS_FIELD_NAME]
          );
          setSelectedSkill(defaultSkill);
          // inform parent of the default skill so Ai Assist button has
          // the correct value even before the user interacts with the dropdown
          if (defaultSkill && onSkillSelect) {
            onSkillSelect(defaultSkill);
          }
        }
      } else {
        addNotification(
          t("error.couldNotGetSkillsListBasedOnSubjectId") +
            ` #. (Status code: ${response.status})`,
          "error"
        );
      }
    }
  }
  useEffect(() => {
    getSkillList();
  }, []);

  return (
    <>
      {skillsList && (
        <div
          className={ClassNames(
            styles.subjectMetricsRow,
            styles.subjectSkillSelectRow
          )}
        >
          <div className={styles.subjectMetricsLabel}>
            {t("growForm.skillToImprove")}
          </div>
          <div className={styles.subjectMetricsContent}>
            <div className={styles.subjectSkillSelectContainer} ref={anchorRef}>
              <Autocomplete
                id="skillSelect1"
                options={skillsList}
                getOptionLabel={({
                  skill_improvement_level_1,
                  skill_improvement_level_2,
                }) => {
                  return `${skill_improvement_level_1}${
                    skill_improvement_level_2
                      ? " — " + skill_improvement_level_2
                      : ""
                  }`;
                }}
                style={{ minWidth: "200px" }}
                onChange={(event, newValue) => {
                  onSkillChange(newValue, event, index);
                  if (
                    newValue &&
                    onNudge &&
                    newValue.id !== selectedSkill?.id
                  ) {
                    onNudge(newValue);
                  }
                  // always tell the parent what is currently selected so the
                  // skill Ai Assist button can use the fresh value even when
                  // MUI Autocomplete doesn't fire onChange for the same value
                  if (onSkillSelect) {
                    onSkillSelect(newValue);
                  }
                  setSelectedSkill(newValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    name={name}
                    placeholder={t("growForm.selectSkill")}
                    variant="outlined"
                  />
                )}
                value={selectedSkill}
                // defaultValue={selectedSkill}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function SubjectMetricTargetInput({
  list,
  setList,
  metricId,
  skillsRequired,
  formValidation,
  hideProjectTargetKpi,
  hideCurrentMetrics,
  isSaveEnabled = false,
  setHasBeenSaved = () => {},
  sessionId,
  aiDisabled = false,
  handleDisableAI,
  handleNudgeRequest,
  popoverAnchorEl: externalPopoverAnchorEl,
  isOpen: externalIsOpen,
  streamedText,
  nudgeType,
  streaming,
  error,
  handleClosePopover,
  handleFeedback,
  handleRefresh,
  ...props
}) {
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [feedbackStates, setFeedbackStates] = useState({});
  const targetInputRefs = useRef({});
  const prevTargetValues = useRef({});
  const skillInputRefs = useRef({});
  const skillSelectRefs = useRef({});
  const prevSkillValues = useRef({});
  // tracks the currently selected skill per subject so the skill Ai Assist
  // button always has the correct value needed because selectedSkill lives
  // inside SkillLists local state and is not accessible from the parent
  const currentSkillRefs = useRef({});
  const activeAnchorRef = useRef({});

  // fieldType: "target" | "skill"
  const handleNudgeClick = (
    subject,
    subjectId,
    anchorEl,
    fieldType = "target"
  ) => {
    if (aiDisabled) return;
    activeAnchorRef.current = anchorEl;

    const questionMap = {
      target: t("growForm.target"),
      skill: t("growForm.skillToImprove"),
    };

    // Pass only the specific field value as the coachee answer.
    const answerMap = {
      target: subject.reality_target,
      skill: subject.skill_improvement_level_1
        ? `${subject.skill_improvement_level_1}${
            subject.skill_improvement_level_2
              ? ` — ${subject.skill_improvement_level_2}`
              : ""
          }`
        : subject[FREETEXT_SKILLS_FIELD_NAME],
    };

    const evaluationText = questionMap[fieldType] ?? questionMap.target;
    // No target fallback here — the empty-field guard already handles empties.
    const coacheeAnswer = answerMap[fieldType];

    setFeedbackStates(prev => {
      const newState = { ...prev };
      // only reset the key for the field being clicked
      const keyToReset =
        fieldType === "skill" ? `${subjectId}_skill` : subjectId;
      delete newState[keyToReset];
      return newState;
    });
    // derive the correct feedbackStates key up front so the callback closure captures it
    const feedbackKey =
      fieldType === "skill" ? `${subjectId}_skill` : subjectId;
    handleNudgeRequest(
      null,
      evaluationText,
      coacheeAnswer,
      anchorEl,
      list.map(item =>
        item.subject_id === subjectId ? { ...subject } : { ...item }
      ),
      type => setFeedbackStates(prev => ({ ...prev, [feedbackKey]: type }))
    );
  };

  const handleLocalRefresh = (item, fieldType) => {
    handleNudgeClick(item, item.subject_id, activeAnchorRef.current, fieldType);
  };

  const handleFieldChange = async (e, index, fieldName) => {
    const { value } = e.target;
    const formattedValue = decimalCheck(value, 2);

    // Update local state
    const updatedList = [...list];
    updatedList[index] = {
      ...updatedList[index],
      [fieldName]: formattedValue,
    };
    setList(updatedList);

    // Sync with Formik
    await formValidation.setFieldValue(
      `subjects.${index}.${fieldName}`,
      formattedValue
    );
    await formValidation.setFieldTouched(
      `subjects.${index}.${fieldName}`,
      true
    );
    await formValidation.validateForm();
  };

  const onSkillChange = (newValue, e, index) => {
    let newList = _.cloneDeep(list);
    newList[index][SKILLS_FIELD_NAME] = newValue ? newValue.id : ""; // reassign value of skill_improve_id
    setList(newList);
    if (localStorage.createSessionFormData) {
      const temp = JSON.parse(localStorage.createSessionFormData);
      if (temp.grow_form_subject_details?.[index]) {
        temp.grow_form_subject_details[index][SKILLS_FIELD_NAME] = newValue
          ? newValue.id
          : "";
        localStorage.setItem("createSessionFormData", JSON.stringify(temp));
      }
    }

    if (localStorage.sessionCoachingFormData) {
      const temp = JSON.parse(localStorage.sessionCoachingFormData);
      if (temp.grow_form_subject_details?.[index]) {
        temp.grow_form_subject_details[index][SKILLS_FIELD_NAME] = newValue
          ? newValue.id
          : "";
        localStorage.setItem("sessionCoachingFormData", JSON.stringify(temp));
      }
    }

    if (localStorage.subjectListData) {
      const temp = JSON.parse(localStorage.subjectListData);
      if (temp.subjectList?.[index]) {
        temp.subjectList[index][SKILLS_FIELD_NAME] = newValue
          ? newValue.id
          : "";
        localStorage.setItem("subjectListData", JSON.stringify(temp));
      }
    }
    if (!!formValidation) {
      formValidation.setFieldValue(
        `subjects.${index}.${SKILLS_FIELD_NAME}`,
        newValue ? newValue.id : ""
      );
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.subjectList}>
        {list.map((item, index) => {
          const { values, errors, touched } = formValidation;

          const subjectErrors = errors?.subjects?.[index];
          const subjectTouched = touched?.subjects?.[index];

          const isTargetMetricError = !!(
            subjectErrors?.[TARGET_FIELD_NAME] &&
            subjectTouched?.[TARGET_FIELD_NAME]
          );
          const isSkillsError = !!(
            (subjectErrors?.[SKILLS_FIELD_NAME] &&
              subjectTouched?.[SKILLS_FIELD_NAME]) ||
            (subjectErrors?.[FREETEXT_SKILLS_FIELD_NAME] &&
              subjectTouched?.[FREETEXT_SKILLS_FIELD_NAME])
          );

          const isLastItem = index === list.length - 1;

          const allTargetsFilled = Array.isArray(values.subjects)
            ? values.subjects.every(subject =>
                Boolean(subject[TARGET_FIELD_NAME])
              )
            : false;

          return (
            <div className={styles.listItem} key={index}>
              <div className={styles.index}>
                <div>{index + 1}</div>
              </div>
              <div className={styles.listItemDetails}>
                <div className={styles.subjectName} title={item.subject}>
                  {item.subject}
                  {item.sub_subject ? " — " + item.sub_subject : ""}
                </div>
                <div className={styles.subjectMetrics}>
                  {!hideProjectTargetKpi && (
                    <div className={styles.subjectMetricsRow}>
                      <div className={styles.projectTargetKpiLabel}>
                        {t("growForm.projectTargetKpi")}
                      </div>
                      <div className={styles.subjectMetricsContent}>
                        <div
                          className={styles.subjectMetricsValue}
                          title={
                            item.project_target_kpi !== "undefined"
                              ? item.project_target_kpi
                              : ""
                          }
                        >
                          <span>
                            {item.project_target_kpi !== "undefined"
                              ? item.project_target_kpi
                              : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!hideProjectTargetKpi && (
                    <div className={styles.subjectMetricsRow}>
                      <div className={styles.projectTargetKpiLabel}>
                        {t("growForm.mtd")}
                      </div>
                      <div className={styles.subjectMetricsContent}>
                        <div
                          className={styles.subjectMetricsValue}
                          title={
                            item.mtd_metric !== "undefined" ? item.metric : ""
                          }
                        >
                          <span>
                            {item.mtd_metric !== "undefined"
                              ? item.mtd_metric
                              : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!hideCurrentMetrics && (
                    <div className={styles.subjectMetricsRow}>
                      <div className={styles.subjectMetricsLabel}>
                        {t("growForm.current")}
                      </div>
                      <div className={styles.subjectMetricsContent}>
                        <div
                          className={styles.subjectMetricsValue}
                          title={item.result_metrics}
                        >
                          <span>{item.result_metrics}</span>
                        </div>

                        {item.document_link &&
                          item.document_link.length > 0 && (
                            <Button
                              className={styles.viewLinkBtn}
                              variant="outlined"
                              name="subjects"
                              disableRipple
                              disableElevation
                              title={item.document_link}
                              onClick={() => {
                                window.open(item.document_link);
                              }}
                            >
                              <LinkIcon className={styles.viewLinkBtnIcon} />
                              <span className={styles.viewLinkBtnLabel}>
                                View link
                              </span>
                            </Button>
                          )}
                      </div>
                    </div>
                  )}
                  {!hideCurrentMetrics && (
                    <div className={styles.subjectMetricsRow}>
                      <div className={styles.subjectMetricsLabel}>
                        {t("growForm.target")}
                      </div>
                      <div className={styles.subjectMetricsContent}>
                        {!(isSaveEnabled && isSaved) && (
                          <FormControl error={isTargetMetricError}>
                            <TextField
                              className={styles.targetMetricsValue}
                              variant="outlined"
                              size="medium"
                              type="number"
                              name={`subjects.${index}.${TARGET_FIELD_NAME}`}
                              placeholder={t("growForm.enterTarget")}
                              defaultValue={list[index][TARGET_FIELD_NAME]}
                              onChange={e =>
                                handleFieldChange(e, index, TARGET_FIELD_NAME)
                              }
                              error={isTargetMetricError}
                              inputRef={el => {
                                targetInputRefs.current[index] = el;
                              }}
                              onFocus={() => {
                                prevTargetValues.current[item.subject_id] =
                                  item.reality_target;
                              }}
                              onBlur={() => {
                                const currentTarget = item.reality_target;
                                if (
                                  currentTarget &&
                                  sessionId &&
                                  currentTarget !==
                                    prevTargetValues.current[item.subject_id]
                                ) {
                                  handleNudgeClick(
                                    item,
                                    item.subject_id,
                                    targetInputRefs.current[index],
                                    "target"
                                  );
                                  prevTargetValues.current[item.subject_id] =
                                    currentTarget;
                                }
                              }}
                            />
                            <AiInsightDiv
                              state={feedbackStates[item.subject_id]}
                              onClick={e => {
                                handleNudgeClick(
                                  item,
                                  item.subject_id,
                                  targetInputRefs.current[index],
                                  "target"
                                );
                              }}
                            />
                            {isTargetMetricError && (
                              <div className="field-error">
                                <FormHelperText>
                                  {subjectErrors?.[TARGET_FIELD_NAME]}
                                </FormHelperText>
                              </div>
                            )}
                          </FormControl>
                        )}
                        {isSaveEnabled && isSaved && (
                          <div className={styles.subjectMetricsValue}>
                            <span>{list[index][TARGET_FIELD_NAME]}</span>
                            <Tooltip title={t("growForm.edit")}>
                              <EditIcon
                                className={styles.editIcon}
                                onClick={() => {
                                  setIsSaved(false);
                                }}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      {sessionId && (
                        <AiPopover
                          open={externalIsOpen && !aiDisabled}
                          anchorEl={externalPopoverAnchorEl}
                          onClose={handleClosePopover}
                          nudgeText={streamedText}
                          nudgeType={nudgeType}
                          streaming={streaming}
                          error={error}
                          // Thumbs only send feedback; the field's indicator
                          // keeps showing the AI's own evaluation (COACH-2169).
                          onFeedback={handleFeedback}
                          onRefresh={() => handleLocalRefresh(item, "target")}
                          onCloseError={handleClosePopover}
                          onDisableAI={handleDisableAI}
                        />
                      )}
                    </div>
                  )}
                  {!hideCurrentMetrics && (
                    <FormControl error={isSkillsError}>
                      {skillsRequired &&
                      item.subjectable_type ===
                        SubjectableType.FLASH_REWARD_USER_KPIS ? (
                        <>
                          <div
                            className={ClassNames(
                              styles.subjectMetricsRow,
                              styles.subjectSkillSelectRow
                            )}
                          >
                            <div className={styles.subjectMetricsLabel}>
                              {t("growForm.skillToImprove")}
                            </div>
                            <TextField
                              required={true}
                              style={{ width: "100%", maxWidth: "300px" }}
                              variant="outlined"
                              name={`subjects.${index}.${FREETEXT_SKILLS_FIELD_NAME}`}
                              onChange={e =>
                                handleFieldChange(
                                  e,
                                  index,
                                  FREETEXT_SKILLS_FIELD_NAME,
                                  false
                                )
                              }
                              inputRef={el => {
                                skillInputRefs.current[index] = el;
                              }}
                              onFocus={() => {
                                prevSkillValues.current[item.subject_id] =
                                  item[FREETEXT_SKILLS_FIELD_NAME];
                              }}
                              onBlur={e => {
                                // read value directly from event to avoid stale state
                                // since handleFieldChange is async and item may not reflect
                                // the latest value yet at the time onBlur fires
                                const currentVal = e.target.value;
                                if (
                                  currentVal &&
                                  sessionId &&
                                  currentVal !==
                                    prevSkillValues.current[item.subject_id]
                                ) {
                                  handleNudgeClick(
                                    {
                                      ...item,
                                      [FREETEXT_SKILLS_FIELD_NAME]: currentVal,
                                    },
                                    item.subject_id,
                                    skillInputRefs.current[index],
                                    "skill"
                                  );
                                  prevSkillValues.current[item.subject_id] =
                                    currentVal;
                                }
                              }}
                            />
                          </div>

                          {isSkillsError && (
                            <div className="field-error">
                              <FormHelperText>
                                {subjectErrors?.[FREETEXT_SKILLS_FIELD_NAME]}
                              </FormHelperText>
                            </div>
                          )}
                          {/* Skill freetext field has its own dedicated Ai Assist button,
                              only shown when skillsRequired is truthy so it doesn't appear
                              on pages where skill to improve is not applicable (e.g. plan page) */}
                          {skillsRequired && (
                            <AiInsightDiv
                              state={feedbackStates[`${item.subject_id}_skill`]}
                              onClick={e => {
                                handleNudgeClick(
                                  item,
                                  item.subject_id,
                                  skillInputRefs.current[index],
                                  "skill"
                                );
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <SkillLists
                            currentMetricId={list[index][metricId]}
                            onSkillChange={onSkillChange}
                            index={index}
                            name={`subjects.${index}.${SKILLS_FIELD_NAME}`}
                            skillsRequired={skillsRequired}
                            anchorRef={el => {
                              skillSelectRefs.current[index] = el;
                            }}
                            onNudge={
                              // nudge now triggers when skill is selected
                              sessionId
                                ? selectedSkill =>
                                    handleNudgeClick(
                                      {
                                        ...item,
                                        skill_improvement_level_1:
                                          selectedSkill?.skill_improvement_level_1,
                                        skill_improvement_level_2:
                                          selectedSkill?.skill_improvement_level_2,
                                      },
                                      item.subject_id,
                                      skillSelectRefs.current[index],
                                      "skill"
                                    )
                                : null
                            }
                            onSkillSelect={
                              // lift selected skill into parent ref so the skill
                              // Ai Assist button always has the current value
                              // needed because selectedSkill lives in SkillLists
                              // local state and is not accessible from the parent
                              selectedSkill => {
                                currentSkillRefs.current[item.subject_id] =
                                  selectedSkill;
                              }
                            }
                          />

                          {isSkillsError && (
                            <div className="field-error">
                              <FormHelperText>
                                {subjectErrors?.[SKILLS_FIELD_NAME]}
                              </FormHelperText>
                            </div>
                          )}
                          {/* Skill dropdown has its own dedicated Ai Assist button,
                              only shown when skillsRequired is truthy so it doesn't appear
                              on pages where skill to improve is not applicable (e.g. plan page)
                              Uses currentSkillRefs to get the currently selected skill since
                              selectedSkill lives inside SkillLists local state */}
                          {skillsRequired && (
                            <AiInsightDiv
                              state={feedbackStates[`${item.subject_id}_skill`]}
                              onClick={e => {
                                const currentSkill =
                                  currentSkillRefs.current[item.subject_id];
                                handleNudgeClick(
                                  {
                                    ...item,
                                    skill_improvement_level_1:
                                      currentSkill?.skill_improvement_level_1,
                                    skill_improvement_level_2:
                                      currentSkill?.skill_improvement_level_2,
                                  },
                                  item.subject_id,
                                  skillSelectRefs.current[index],
                                  "skill"
                                );
                              }}
                            />
                          )}
                        </>
                      )}
                    </FormControl>
                  )}
                  {isSaveEnabled && !isSaved && isLastItem && (
                    <div className={styles.saveButtonContainer}>
                      <div className={styles.buttonSpacer} />
                      <div className={styles.subjectMetricsContent}>
                        <Button
                          className={styles.saveButton}
                          variant="outlined"
                          color="primary"
                          disabled={!allTargetsFilled}
                          onClick={() => {
                            formValidation.validateForm().then(errors => {
                              if (
                                !errors.subjects ||
                                Object.keys(errors.subjects).length === 0
                              ) {
                                setIsSaved(true);
                                setHasBeenSaved(true);
                              }
                            });
                          }}
                        >
                          {t("growForm.save")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
